/**
 * BrokerSection Component
 *
 * Single-broker section presented as a "button + capture modal", mirroring
 * the Collateral / Stamp Duty sections for visual consistency:
 *
 *  - When no broker is selected the section header shows "Broker (0)" and
 *    an "Add Broker" button; below is an empty-state Paper.
 *  - When a broker is selected the header shows "Broker (1)", a single-row
 *    table renders the broker / fee % / computed amount with edit + remove
 *    actions. The Add button is hidden (single-broker constraint).
 *
 * The broker FEE is wired to reference data: the modal has a "Broker Fee"
 * dropdown listing the `Broker Fee`-type rows from Reference Data → Placement
 * Fees. Selecting a row sets placement_fees_id (whose GL expense/control
 * accounts drive the commission journal export) and pre-fills the % field,
 * which the user can then override. Broker fee is an upfront, one-off fee =
 * capital_amount × %.
 *
 * The form fields broker_id / broker_fees / placement_fees_id remain
 * authoritative — the modal just writes into them via setValue. is_brokered is
 * kept in sync by an effect on broker_id presence so validation in
 * useContractValidation.js continues to apply.
 *
 * Stamp duty is NOT handled here (T112): it is a standalone option captured by
 * StampDutySection.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { addMonths } from 'date-fns';
import { useFormContext } from 'react-hook-form';
import { useContractFormContext } from '../ContractFormContext';
import StakeholderAutocomplete from '../../../common/StakeholderAutocomplete';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { InlineGateToggle } from '../../../common/LabeledSwitch';
import { formatNumber, formatRatePct } from '../../../../utils/formatters';

// T3-084: broker-fee VAT treatment. STANDARD applies the contract country's rate (the
// pre-T3-084 behaviour and the default); NO_VAT is an explicit 0% for a cross-border fee —
// e.g. a broker domiciled in eSwatini on an SZL contract whose fee is settled from SA in ZAR.
// No derived signal can detect that, hence the explicit choice. Codes match the DB CHECK.
const VAT_TREATMENT_STANDARD = 'STANDARD';
const VAT_TREATMENT_NO_VAT = 'NO_VAT';

// Feature 30 sentinels. refdata_frequency.months_value has CHECK (> 0), so a literal 0 for
// "once-off" is impossible; the existing At Maturity (999) row is reinterpreted IN FEE TERMS
// instead, and relabelled below. Adhoc (998) means "manual dates only" and is not a cadence the
// fee engine can step, so it is excluded from the picker.
//
// ⚠ THE RELABEL IS SCOPED TO THIS SCREEN ONLY. "At Maturity" is the correct term on the interest
// and capital frequency pickers; renaming the refdata row, or using a shared formatter, would
// mislead every one of them. A new 997 sentinel was rejected outright: seven guard sites across
// the amortization engine test `< 998` / `>= 998` and would read 997 as a valid 997-month cadence.
const MONTHS_AT_MATURITY = 999;
const MONTHS_ADHOC = 998;

const EMPTY_FORM = {
  broker_id: '',
  placement_fees_id: '',
  broker_fees: '',
  broker_fee_vat_treatment: VAT_TREATMENT_STANDARD,
  broker_fee_settled_in_other_ccy: false,
  broker_fee_settlement_currency_id: '',
  broker_fee_vat_note: '',
  // Feature 30 — fee scheduling.
  //
  // ⚠ AN EMPTY CADENCE MEANS "SETTLE THE WHOLE FEE IN ONE PAYMENT" — it does NOT mean "no
  // schedule". The expense is accrued over the contract term regardless, because that is the GL
  // requirement: the fee is raised once, recognised over the term. So an empty cadence still
  // yields N accrual rows (N = tenor in months) plus a single payment row.
  //
  // ⚠ A comment here once said an empty cadence meant no accrual rows and no payment events at
  // all. That was wrong — it would leave a once-off upfront fee with its expense never spread.
  // The engine matches this reading; see the ALWAYS-SCHEDULED lock in tests/feeService.test.js.
  fee_frequency_id: '',
  next_fee_date: '',
  // TRUE = paid at the BEGINNING. Fees are usually paid upfront, so that is the default
  // (user, 2026-09-11). ⚠ This deliberately differs from the DB column default
  // (paid_in_advance BOOLEAN DEFAULT false = arrear) — the UI always sends an explicit value,
  // so the column default only applies to a row written without this field.
  paid_in_advance: true,
  repricing_frequency_id: '',
  next_repricing_date: '',
};

// Fixed widths so the fee% + Amount columns sit tight together and Actions
// anchors at the same right-edge position as the Stamp Duty / Collateral tables.
const PCT_COL_W = 120;
// Narrower per-column width so the three amount columns (Excl / VAT / Incl) fit
// where the single Amount column used to sit.
const AMT_SUB_COL_W = 130;
const ACTIONS_COL_W = 96;

function BrokerSection() {
  const { watch, setValue } = useFormContext();
  const { refData, appendToRefData, initialData, isEditable, enableBrokerFeeScheduling } = useContractFormContext();

  const brokerId = watch('broker_id');
  const brokerFees = watch('broker_fees');
  const placementFeesId = watch('placement_fees_id');
  const isBrokered = !!brokerId;
  const disabled = isEditable === false;

  // Contract capital + currency for the computed (capital × %) amount.
  const capitalAmount = parseFloat(watch('capital_amount')) || 0;
  const investmentCurrencyId = watch('investment_currency_id');
  const currencyCode = (refData.currencies || []).find(c => c.id === investmentCurrencyId)?.currency_code || '';

  // Available Broker Fee reference rows (type = 'Broker Fee', active).
  const brokerFeeOptions = (refData.placementFees || []).filter(
    (f) => f.type === 'Broker Fee' && f.active !== false
  );

  // T3-012: VAT rate for the contract's country (investment currency country).
  // One current rate per country; absent => no VAT configured (treat as 0, but
  // surface a hint rather than silently dropping VAT).
  const vatRow = (refData.vatRates || []).find(
    (v) => v.country_id === investmentCurrencyId && v.active !== false
  );
  const vatPercent = vatRow ? parseFloat(vatRow.vat_rate) : null;
  const hasVatRate = vatPercent != null && isFinite(vatPercent);

  // T3-084: the saved treatment drives the inline table row; the dialog passes its draft
  // treatment explicitly so the breakdown reacts before the form is written.
  const formVatTreatment = watch('broker_fee_vat_treatment') || VAT_TREATMENT_STANDARD;

  // Effective VAT % for a treatment. NO_VAT is a deliberate tax decision, so it is a hard 0
  // regardless of whether the country happens to have a rate configured.
  const effectiveVatPercent = (treatment) => {
    if (treatment === VAT_TREATMENT_NO_VAT) return 0;
    return hasVatRate ? vatPercent : 0;
  };

  // Derive VAT amount + VAT-inclusive total from an exclusive amount.
  const computeVatAmount = (exclusive, treatment = formVatTreatment) => {
    if (exclusive == null || !isFinite(exclusive)) return null;
    return exclusive * effectiveVatPercent(treatment) / 100;
  };
  const computeInclusive = (exclusive, treatment = formVatTreatment) => {
    if (exclusive == null || !isFinite(exclusive)) return null;
    return exclusive + (computeVatAmount(exclusive, treatment) || 0);
  };

  // The Standard option must NAME the rate it will apply. With no refdata_vat_rates row for
  // the contract country there is no rate — say so explicitly rather than rendering "(0%)",
  // which would make Standard visually identical to No-VAT and hide the fact that this is a
  // configuration gap, not a tax decision.
  const standardVatLabel = hasVatRate
    ? `Standard-Rate VAT (${formatRatePct(vatPercent)}%)`
    : 'Standard-Rate VAT (no rate configured for this country)';

  // Sync is_brokered with broker_id presence. Validation in
  // useContractValidation.js still keys off is_brokered.
  useEffect(() => {
    setValue('is_brokered', !!brokerId);
  }, [brokerId, setValue]);

  // Dialog state — separate from the live form so cancel doesn't mutate it.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_FORM });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: null });

  const computeAmount = (pct) => {
    const p = parseFloat(pct);
    if (!isFinite(p)) return null;
    return capitalAmount * p / 100;
  };

  const handleOpenDialog = (mode) => {
    if (mode === 'edit') {
      // Seed placement_fees_id from the form; fall back to matching the saved
      // % to a Broker Fee row so an edit still pre-selects the dropdown.
      let pfId = placementFeesId || '';
      if (!pfId && brokerFees !== '' && brokerFees != null) {
        const match = brokerFeeOptions.find(
          (o) => Math.abs(parseFloat(o.percentage) - parseFloat(brokerFees)) < 0.00005
        );
        if (match) pfId = match.id;
      }
      setDraft({
        broker_id: brokerId || '',
        placement_fees_id: pfId,
        // Normalize to a trimmed value so the field shows 1.5 not 1.5000.
        broker_fees: formatRatePct(brokerFees),
        // T3-084: seed the treatment + its companions from the form. A contract saved before
        // T3-084 carries no treatment, so it opens on Standard-Rate — its historic behaviour.
        broker_fee_vat_treatment: watch('broker_fee_vat_treatment') || VAT_TREATMENT_STANDARD,
        broker_fee_settled_in_other_ccy: watch('broker_fee_settled_in_other_ccy') === true,
        broker_fee_settlement_currency_id: watch('broker_fee_settlement_currency_id') || '',
        broker_fee_vat_note: watch('broker_fee_vat_note') || '',
        // Feature 30 — seed the cadence from the form, which ContractWizard loaded from the
        // BROKER FEE endpoint, not from the contract object.
        //
        // ⚠ NEVER seed these from `contract.repricing_frequency_id`. That column exists on
        // contract_master and is the RATE repricing cadence (Interest & Payments step); the fee
        // repricing cadence is a DIFFERENT column of the same name on contract_broker_fees, and
        // fn_contract_get_by_id returns only the contract_master one. Reading the contract object
        // here would silently show the rate cadence on the fee screen and save it back as the fee
        // cadence — with no error, since both are NULL on every contract today.
        fee_frequency_id: watch('broker_fee_frequency_id') || '',
        next_fee_date: watch('broker_fee_next_date') || '',
        paid_in_advance: watch('broker_fee_paid_in_advance') !== false,
        repricing_frequency_id: watch('broker_fee_repricing_frequency_id') || '',
        next_repricing_date: watch('broker_fee_next_repricing_date') || '',
      });
      // ⚠ OPENING A SAVED FEE IS NOT AN OVERRIDE. Only a date TYPED in this dialog session counts
      // as manual, so changing the frequency on an existing fee re-derives the anchor as expected.
      //
      // An earlier cut flagged any saved date as user-owned. That showed "Manually set — it will
      // not follow the cadence" on a fee the user had merely opened, and then refused to move the
      // date when they switched to Quarterly — the exact behaviour this feature exists to fix.
      // Every saved fee has a date (the DB CHECK requires one alongside a cadence), so that test
      // was true for literally every edit and the derivation could never run.
      setNextFeeDateOverridden(false);
    } else {
      setDraft({ ...EMPTY_FORM });
      setNextFeeDateOverridden(false);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDraft({ ...EMPTY_FORM });
    setNextFeeDateOverridden(false);
  };

  // Selecting a Broker Fee row sets the ref id and (re)pre-fills the % from that
  // row. Choosing a different row is a deliberate new selection, so it always
  // refreshes the %; the user can then override by typing in the % field after.
  const handleSelectFee = (feeId) => {
    const selected = brokerFeeOptions.find((o) => o.id === feeId);
    setDraft((prev) => ({
      ...prev,
      placement_fees_id: feeId,
      broker_fees: selected ? parseFloat(selected.percentage) : prev.broker_fees,
    }));
  };

  // T3-084: switching back to Standard clears the companions in the draft, so a user who
  // toggles No-VAT -> fills the currency -> switches back -> saves cannot ship orphaned facts
  // (which the SP would strip anyway). Keeps the UI honest with what the DB will hold.
  const handleSelectVatTreatment = (treatment) => {
    setDraft((prev) => ({
      ...prev,
      broker_fee_vat_treatment: treatment,
      ...(treatment === VAT_TREATMENT_STANDARD
        ? {
          broker_fee_settled_in_other_ccy: false,
          broker_fee_settlement_currency_id: '',
          broker_fee_vat_note: '',
        }
        : {}),
    }));
  };

  // T3-084: the flag OWNS the currency — un-ticking clears it so a stale pick can never be
  // saved, and the SP/CHECK would strip it anyway.
  const handleToggleSettledInOtherCcy = (checked) => {
    setDraft((prev) => ({
      ...prev,
      broker_fee_settled_in_other_ccy: checked,
      ...(checked ? {} : { broker_fee_settlement_currency_id: '' }),
    }));
  };

  // ---- Feature 30: fee scheduling -------------------------------------------------------
  //
  // Both pickers read the SAME refdata_frequency feed the interest/capital pickers use, filtered
  // for this screen's meaning. `active !== false` matters: Bi-Monthly is inactive on dev, and an
  // inactive row must not be offered.
  // The contract's term start, used to prefill the fee anchor.
  //
  // ⚠ PRECEDENCE MIRRORS feeService.resolveTermStart EXACTLY — interest_start_date, then
  // funds_received_date, then deposit_date. If these two ever disagree, the date shown here is
  // not the date the engine anchors on, and the user would be editing a figure that silently
  // does nothing. (interest_start_date is not a wizard form field — it is resolved server-side
  // once a schedule exists — so in the wizard the first available is funds_received_date.)
  // Both wizard fields are already stored as 'yyyy-MM-dd', which is what input[type=date] needs.
  const contractTermStart = watch('funds_received_date') || watch('deposit_date') || '';

  const activeFrequencies = (refData.frequencies || []).filter((f) => f && f.active !== false);

  // BOTH sentinels are excluded, for different reasons:
  //   Adhoc (998)       — "manual dates only", not a cadence the engine can step.
  //   At Maturity (999) — would render as "Once-Off", which CONTRADICTS the gate it sits inside.
  //
  // ⚠ WHY 999 WAS REMOVED (user, 2026-09-17): "if it's paid once-off it's not an instalment, so
  // why would we toggle instalments to pay once off". Exactly right, and the engine proves the
  // option was pure duplication — `generateFeePaymentDates` handles Once-Off(999) and "no cadence"
  // in the SAME branch (`months === null` -> a single event), so the two states were byte-
  // identical: 1 payment, same date, same 12 accruals, measured both in advance and in arrear.
  // So "toggle ON + Once-Off" was a second, self-contradictory way to spell "toggle OFF".
  //
  // The distinction is now clean:
  //   toggle OFF = ONE payment for the whole fee (the engine's no-cadence path)
  //   toggle ON  = a genuine instalment cadence — Monthly / Quarterly / Bi-Annual / Annual
  // The expense is accrued over the term either way; only the payment profile differs.
  //
  // Safe with no migration: 999 is used by ZERO fees on all three dev tenants (6 are cadence-NULL
  // legacy fees, 1 is Quarterly), so no saved fee can reference an option the list no longer
  // offers. refdata_frequency is untouched — "At Maturity" remains correct on the interest and
  // capital pickers, which is why this filter is local to the fee screen.
  const feeFrequencyOptions = activeFrequencies.filter(
    (f) => f.months_value !== MONTHS_ADHOC && f.months_value !== MONTHS_AT_MATURITY
  );

  // Repricing excludes BOTH sentinels: "reprice at maturity" and "reprice adhoc" are not
  // schedulable cadences — the same exclusion the rate-change repricing picker applies.
  // Kept as its own constant even though it now filters identically to the fee list above: the
  // two answer different questions ("how often is the fee PAID" vs "how often is it RENEGOTIATED")
  // and exclude 999 for different reasons, so collapsing them would couple two unrelated rules.
  const repricingFrequencyOptions = activeFrequencies.filter(
    (f) => f.months_value !== MONTHS_ADHOC && f.months_value !== MONTHS_AT_MATURITY
  );

  // ⚠ THE GATE'S STATE IS DERIVED, NOT STORED. "Scheduling is on" IS "a cadence is set" — there
  // is no eligible_for_fee_scheduling column, deliberately (see the block comment in the JSX), so
  // the flag and the data cannot drift apart. The consequence, accepted: turning the gate on has
  // to choose a default cadence, because an "on" state with no cadence has nowhere to live.
  const feeSchedulingOn = !!draft.fee_frequency_id;

  // ===========================================================================================
  // THE FIRST FEE PAYMENT DATE, DERIVED FROM THE CADENCE + TIMING (user, 2026-09-15).
  //
  // "the fee is set to quarterly so if paid at beginning would be at start of contract, but if
  // paid at [end] it should determine the next date, same as what we do for interest payments."
  //
  // The engine already behaves exactly this way — on contract 376 (13/08/2026 → 13/08/2027,
  // quarterly) it pays 13/08 · 13/11 · 13/02 · 13/05 in advance, and 12/11 · 12/02 · 12/05 ·
  // 12/08 in arrear. The dialog, however, used to seed the term start REGARDLESS of timing, so an
  // arrear fee displayed a date the engine would never pay on.
  //
  //   in advance -> the period OPENS with a payment  -> the term start
  //   in arrear  -> the period CLOSES with a payment -> addMonths(termStart, m) - 1 day
  //
  // ⚠ USE date-fns addMonths — NOT hand-rolled Date arithmetic. Plain UTC month arithmetic
  // OVERFLOWS on month-end (31 Jan + 1 month = 2 Mar) while date-fns CLAMPS to the last valid day
  // (28 Feb), and the fee engine itself uses date-fns (feeService.js:85). Verified against the
  // engine's own first payment date across advance/arrear × monthly/quarterly × ordinary,
  // month-end and Jan–Dec terms: the clamped form matches in every case, the overflowing one is
  // two days out on a 31 Jan contract.
  //
  // ⚠ Returns '' when it cannot be computed (no term start, or no resolvable month count) rather
  // than guessing — a wrong anchor is worse than an empty field the user must fill, and the DB
  // CHECK (chk_cbf_fee_freq_requires_date) will not accept a cadence without one either way.
  const deriveNextFeeDate = (frequencyId, paidInAdvance) => {
    if (!contractTermStart || !frequencyId) return '';
    if (paidInAdvance !== false) return contractTermStart;

    const freq = feeFrequencyOptions.find((f) => f.id === frequencyId);
    const months = Number(freq?.months_value);
    // Once-Off (999) has no periodic cadence to step: in arrear it settles at maturity, which the
    // engine resolves itself. Leave the anchor on the term start rather than inventing a date
    // ~999 months away.
    if (!Number.isFinite(months) || months <= 0 || months === MONTHS_AT_MATURITY) return contractTermStart;

    // Parse as UTC midnight so the calendar day cannot shift under a local timezone, then read
    // back the UTC fields — the same discipline feeService/amortizationService apply.
    const start = new Date(`${contractTermStart}T00:00:00Z`);
    if (Number.isNaN(start.getTime())) return contractTermStart;
    const close = new Date(addMonths(start, months).getTime() - 86400000);
    return `${close.getUTCFullYear()}-${String(close.getUTCMonth() + 1).padStart(2, '0')}-${String(close.getUTCDate()).padStart(2, '0')}`;
  };

  // ⚠ ONCE THE USER TYPES A DATE, STOP DERIVING. Without this, editing the anchor and then
  // changing the frequency (or flipping the timing switch) would silently discard the edit. Same
  // shape as `fundsReceivedOverridden` on the contract-details step, which guards the identical
  // hazard for the funds-received date.
  const [nextFeeDateOverridden, setNextFeeDateOverridden] = useState(false);

  // Turning the gate ON seeds the most common cadence (Monthly) so the block opens in a usable
  // state rather than an invalid one — every other field then has something to anchor to.
  // Turning it OFF clears EVERYTHING in the block, exactly as the Eligible-for-renewal gate does
  // (InterestPaymentsStep.js:1225): a half-cleared block would leave a stale date that the DB
  // CHECKs permit (they allow a date with no frequency) and that would silently reappear if the
  // cadence were ever re-set.
  const handleToggleFeeScheduling = (checked) => {
    if (checked) {
      const monthly = feeFrequencyOptions.find((f) => f.months_value === 1);
      const freqId = monthly ? monthly.id : (feeFrequencyOptions[0]?.id ?? '');
      setDraft((prev) => ({
        ...prev,
        fee_frequency_id: freqId,
        // Derived from the cadence + timing rather than blindly seeded with the term start — see
        // deriveNextFeeDate. A hand-typed value still wins.
        next_fee_date: nextFeeDateOverridden && prev.next_fee_date
          ? prev.next_fee_date
          : deriveNextFeeDate(freqId, prev.paid_in_advance),
      }));
    } else {
      setDraft((prev) => ({
        ...prev,
        fee_frequency_id: '',
        next_fee_date: '',
        paid_in_advance: true,
        repricing_frequency_id: '',
        next_repricing_date: '',
      }));
      // Clearing the block also clears the "user owns this date" flag, so re-enabling scheduling
      // starts from a derived value again rather than resurrecting a stale manual entry.
      setNextFeeDateOverridden(false);
    }
  };

  // Clearing a cadence must clear its anchor date, or the pair is left half-set: the DB CHECKs
  // (chk_cbf_fee_freq_requires_date / chk_cbf_repricing_requires_date) permit a date without a
  // frequency, so a stale date would persist invisibly and reappear if the cadence were re-set.
  const handleSelectFeeFrequency = (value) => {
    setDraft((prev) => ({
      ...prev,
      fee_frequency_id: value,
      // Changing the cadence moves the first payment date, so re-derive it — the whole point of
      // the user's report: a quarterly arrear fee must not keep showing the term start.
      ...(value
        ? (nextFeeDateOverridden ? {} : { next_fee_date: deriveNextFeeDate(value, prev.paid_in_advance) })
        : { next_fee_date: '', paid_in_advance: true }),
    }));
    if (!value) setNextFeeDateOverridden(false);
  };

  // Flipping beginning/end changes which END of the period carries the payment, so the anchor is
  // re-derived on the same rule. `checked` on the switch means "paid at the END" (the switch is
  // deliberately inverted relative to the column — see the JSX comment), hence paid_in_advance is
  // its negation.
  const handleToggleFeeTiming = (checkedMeansArrear) => {
    const paidInAdvance = !checkedMeansArrear;
    setDraft((prev) => ({
      ...prev,
      paid_in_advance: paidInAdvance,
      ...(prev.fee_frequency_id && !nextFeeDateOverridden
        ? { next_fee_date: deriveNextFeeDate(prev.fee_frequency_id, paidInAdvance) }
        : {}),
    }));
  };

  const handleSelectRepricingFrequency = (value) => {
    setDraft((prev) => ({
      ...prev,
      repricing_frequency_id: value,
      ...(value ? {} : { next_repricing_date: '' }),
    }));
  };

  const isSubmitDisabled = () => {
    if (!draft.broker_id) return true;
    if (!draft.placement_fees_id) return true;
    const fees = parseFloat(draft.broker_fees);
    if (!isFinite(fees) || fees < 0) return true;
    // T3-084: the currency is required only when the user has asserted the fee is settled in
    // a different currency — that is the one case where "which currency" is a real question.
    if (draft.broker_fee_vat_treatment === VAT_TREATMENT_NO_VAT
      && draft.broker_fee_settled_in_other_ccy === true
      && !draft.broker_fee_settlement_currency_id) return true;
    // Feature 30: a cadence without its anchor date is refused by the DB
    // (chk_cbf_fee_freq_requires_date / chk_cbf_repricing_requires_date, both 23514). 23514 is
    // NOT mapped in middleware/errorHandler.js, so it would surface as an opaque 500 rather than
    // a 400 — block it here, where the field can be pointed at.
    if (draft.fee_frequency_id && !draft.next_fee_date) return true;
    if (draft.repricing_frequency_id && !draft.next_repricing_date) return true;
    return false;
  };

  const handleSubmit = () => {
    if (isSubmitDisabled()) return;
    const fees = parseFloat(draft.broker_fees);
    setValue('broker_id', parseInt(draft.broker_id, 10));
    setValue('placement_fees_id', parseInt(draft.placement_fees_id, 10));
    setValue('broker_fees', fees);
    // T3-012: snapshot the VAT % (from the contract country) and the three
    // derived amounts so the contract is a stable record. No VAT rate for the
    // country => 0% VAT, exclusive == inclusive.
    // T3-084: a No-VAT treatment snapshots a HARD 0% — it must not record the country rate,
    // or a later reader cannot distinguish a cross-border override from a domestic fee.
    //
    // *** BUSINESS RULE (2026-07-29) — BROKER FEES ARE STRUCK ONCE, AT SUBMISSION. ***
    // This is the ONLY place the broker-fee snapshot is written, and that is DELIBERATE. The fee is
    // the amount agreed with the broker when the contract was submitted; a LATER change to
    // capital_amount does NOT re-strike it, because the broker earned the fee on the original
    // amount. Capital lives on a different wizard step, so it can and does change afterwards.
    //
    // CONSEQUENCE: a contract whose stored broker_fee_exclusive_amount disagrees with
    // capital_amount x broker_fees% is EXPECTED and CORRECT — not stale, not a bug. Real example:
    // MTN-BW-2026-0002 holds a 400,000 fee struck on 20,000,000 of capital that was later reduced
    // to 150,000.
    //
    // THEREFORE: do NOT add a recompute-on-save (here, in buildPayload, or in sp_contract_update),
    // and do NOT "repair" such rows with a data migration. Both were proposed and rejected — they
    // would overwrite correct, commercially-agreed figures. To change an agreed fee, a user re-opens
    // THIS dialog and clicks Update, which is an explicit act.
    const treatment = draft.broker_fee_vat_treatment || VAT_TREATMENT_STANDARD;
    const isNoVat = treatment === VAT_TREATMENT_NO_VAT;
    const exclusive = computeAmount(fees);
    setValue('broker_fee_vat_percent', isNoVat ? 0 : (hasVatRate ? vatPercent : 0));
    setValue('broker_fee_exclusive_amount', exclusive);
    setValue('broker_fee_vat_amount', computeVatAmount(exclusive, treatment));
    setValue('broker_fee_inclusive_amount', computeInclusive(exclusive, treatment));
    setValue('broker_fee_vat_treatment', treatment);
    const settledInOther = isNoVat && draft.broker_fee_settled_in_other_ccy === true;
    setValue('broker_fee_settled_in_other_ccy', settledInOther);
    // The currency belongs to the flag, so it is only persisted when the flag is set.
    setValue('broker_fee_settlement_currency_id', settledInOther ? (draft.broker_fee_settlement_currency_id || '') : '');
    setValue('broker_fee_vat_note', isNoVat ? (draft.broker_fee_vat_note || '').trim() : '');
    // Feature 30 — fee scheduling. Deliberately OUTSIDE the struck-once block above: these do not
    // touch the agreed amount, they only decide when it is accrued and paid, so re-saving them is
    // not a re-strike. Written as '' rather than null when unset, matching every other optional
    // field here; ContractWizard normalises to null when it builds the junction payload.
    setValue('broker_fee_frequency_id', draft.fee_frequency_id || '');
    setValue('broker_fee_next_date', draft.next_fee_date || '');
    setValue('broker_fee_paid_in_advance', draft.fee_frequency_id ? draft.paid_in_advance === true : true);
    setValue('broker_fee_repricing_frequency_id', draft.repricing_frequency_id || '');
    setValue('broker_fee_next_repricing_date', draft.next_repricing_date || '');
    handleCloseDialog();
  };

  const clearBrokerFields = () => {
    setValue('broker_id', '');
    setValue('placement_fees_id', '');
    setValue('broker_fees', '');
    setValue('broker_fee_vat_percent', '');
    setValue('broker_fee_exclusive_amount', '');
    setValue('broker_fee_vat_amount', '');
    setValue('broker_fee_inclusive_amount', '');
    // T3-084: reset the treatment to STANDARD, not '' — it backs a NOT NULL column.
    setValue('broker_fee_vat_treatment', VAT_TREATMENT_STANDARD);
    setValue('broker_fee_settled_in_other_ccy', false);
    setValue('broker_fee_settlement_currency_id', '');
    setValue('broker_fee_vat_note', '');
    // Feature 30: removing the broker removes the fee, so its schedule goes too. Leaving a
    // cadence behind on a contract with no broker would let the engine generate accruals for a
    // fee that no longer exists.
    setValue('broker_fee_frequency_id', '');
    setValue('broker_fee_next_date', '');
    setValue('broker_fee_paid_in_advance', true);
    setValue('broker_fee_repricing_frequency_id', '');
    setValue('broker_fee_next_repricing_date', '');
  };

  const handleRemove = () => {
    setConfirmDialog({
      open: true,
      onConfirm: () => {
        clearBrokerFields();
        setConfirmDialog({ open: false, onConfirm: null });
      },
    });
  };

  const brokerName = (refData.brokers || []).find(b => b.id === brokerId)?.name
    || initialData?.broker_name
    || '—';

  const draftFeeRow = brokerFeeOptions.find((o) => o.id === draft.placement_fees_id);
  // Row amounts: exclusive (capital × %), the VAT on it, and the VAT-inclusive
  // total — the same three values the Add/Edit dialog's breakdown card shows.
  const rowExclusive = computeAmount(brokerFees);
  const rowVat = computeVatAmount(rowExclusive);
  const rowInclusive = computeInclusive(rowExclusive);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">
            Broker ({isBrokered ? 1 : 0})
          </Typography>
        </Box>
        {!disabled && !isBrokered && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            Add Broker
          </Button>
        )}
      </Box>

      {!isBrokered ? (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography color="textSecondary">
            No broker on this contract. Click "Add Broker" to record one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            {/* 8-column grid: the broker Amount is split into Excl / VAT / Incl,
                consuming two of the blank filler columns the sibling Stamp Duty /
                Collateral tables use, so Actions stays anchored at the right edge.
                Columns 2–3 remain blank fillers. */}
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Broker</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: PCT_COL_W }}>Broker Fee %</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: AMT_SUB_COL_W }}>Amount (Excl)</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: AMT_SUB_COL_W }}>Amount (VAT)</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: AMT_SUB_COL_W }}>Amount (Incl)</TableCell>
                {!disabled && <TableCell align="center" sx={{ width: ACTIONS_COL_W, minWidth: ACTIONS_COL_W }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight="medium">{brokerName}</Typography>
                  </Box>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right" sx={{ width: PCT_COL_W }}>
                  <Typography variant="body2">
                    {brokerFees !== '' && brokerFees != null ? `${formatRatePct(brokerFees)}%` : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ width: AMT_SUB_COL_W }}>
                  <Typography variant="body2">
                    {rowExclusive != null ? `${currencyCode} ${formatNumber(rowExclusive)}` : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ width: AMT_SUB_COL_W }}>
                  <Typography variant="body2">
                    {rowVat != null ? `${currencyCode} ${formatNumber(rowVat)}` : '—'}
                  </Typography>
                  {/* T3-084: without this a 0.00 is ambiguous between a No-VAT override, a
                      missing country rate, and a configured 0% rate. */}
                  {formVatTreatment === VAT_TREATMENT_NO_VAT && (
                    <Chip label="No-VAT" size="small" variant="outlined" sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }} />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ width: AMT_SUB_COL_W }}>
                  <Typography variant="body2">
                    {rowInclusive != null ? `${currencyCode} ${formatNumber(rowInclusive)}` : '—'}
                  </Typography>
                </TableCell>
                {!disabled && (
                  <TableCell align="center" sx={{ width: ACTIONS_COL_W, minWidth: ACTIONS_COL_W }}>
                    <Tooltip title="Edit broker">
                      <IconButton size="small" onClick={() => handleOpenDialog('edit')}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove broker">
                      <IconButton size="small" color="error" onClick={handleRemove}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* md, not sm: the dialog carries TWO columns (broker + fee capture on the left, fee
          scheduling on the right). At sm everything stacked into one tall column and the dialog
          scrolled, which is what made the scheduling block feel buried. */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{brokerId ? 'Edit Broker' : 'Add Broker'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* LEFT COLUMN — who the broker is and what the fee is.
                Split into two columns so the dialog stops scrolling: the capture fields and the
                scheduling fields are independent questions, and stacking them made the second
                one feel buried below the fold. */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Grid container spacing={3}>
            <Grid size={12}>
              <StakeholderAutocomplete
                stakeholderType="broker"
                options={refData.brokers}
                value={draft.broker_id}
                onChange={(id) => setDraft(prev => ({ ...prev, broker_id: id }))}
                onStakeholderCreated={(newBroker) => {
                  appendToRefData('brokers', newBroker);
                  setDraft(prev => ({ ...prev, broker_id: newBroker.id }));
                }}
                label="Broker *"
                countries={refData.currencies}
              />
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="broker-fee-ref-label">Broker Fee *</InputLabel>
                <Select
                  labelId="broker-fee-ref-label"
                  label="Broker Fee *"
                  value={draft.placement_fees_id}
                  onChange={(e) => handleSelectFee(e.target.value)}
                >
                  {brokerFeeOptions.length === 0 && (
                    <MenuItem value="" disabled>
                      No broker-fee rates in Reference Data → Placement Fees
                    </MenuItem>
                  )}
                  {brokerFeeOptions.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.reference} — {formatRatePct(o.percentage)}%
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                label="Broker Fees (%) *"
                type="number"
                fullWidth
                value={draft.broker_fees}
                onChange={(e) => setDraft(prev => ({ ...prev, broker_fees: e.target.value }))}
                inputProps={{ step: 0.0001, min: 0 }}
                helperText={
                  draftFeeRow
                    ? `Pre-filled from reference data (${formatRatePct(draftFeeRow.percentage)}%) — editable`
                    : 'Pre-filled from reference data when a broker fee is selected — editable'
                }
              />
            </Grid>

            {/* T3-084: VAT treatment sits directly after the % (it modifies how the % is
                grossed up) and ABOVE the breakdown card, so the user sees the control and
                then its effect — not numbers changing above the control that changed them. */}
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="broker-vat-treatment-label">VAT Treatment</InputLabel>
                <Select
                  labelId="broker-vat-treatment-label"
                  label="VAT Treatment"
                  value={draft.broker_fee_vat_treatment || VAT_TREATMENT_STANDARD}
                  onChange={(e) => handleSelectVatTreatment(e.target.value)}
                >
                  <MenuItem value={VAT_TREATMENT_STANDARD}>{standardVatLabel}</MenuItem>
                  <MenuItem value={VAT_TREATMENT_NO_VAT}>No-VAT (0%)</MenuItem>
                </Select>
                {/* Only the NO-VAT branch says anything the control does not: "Standard-Rate VAT
                    (15%)" already states the rate, so its helper was pure restatement. */}
                {draft.broker_fee_vat_treatment === VAT_TREATMENT_NO_VAT && (
                  <FormHelperText>
                    No VAT on this fee — e.g. a cross-border fee settled outside this contract’s country.
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {draft.broker_fee_vat_treatment === VAT_TREATMENT_NO_VAT && (
              <>
                <Grid size={12}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={draft.broker_fee_settled_in_other_ccy === true}
                        onChange={(e) => handleToggleSettledInOtherCcy(e.target.checked)}
                      />
                    )}
                    label="Fee is settled in a different currency"
                  />
                </Grid>
                {/* T3-084: the currency picker only appears once the flag is ticked — if the
                    fee is NOT in a different currency there is nothing to pick, and showing a
                    live pre-filled picker under an unticked box reads as a contradiction. */}
                {draft.broker_fee_settled_in_other_ccy === true && (
                  <Grid size={12}>
                    <FormControl fullWidth>
                      <InputLabel id="broker-vat-settlement-ccy-label">Settled in *</InputLabel>
                      <Select
                        labelId="broker-vat-settlement-ccy-label"
                        label="Settled in *"
                        value={draft.broker_fee_settlement_currency_id}
                        onChange={(e) => setDraft(prev => ({ ...prev, broker_fee_settlement_currency_id: e.target.value }))}
                      >
                        {(refData.currencies || [])
                          .filter((c) => c.currency_code && c.active !== false)
                          .map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.currency_code} — {c.country}
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText>
                        {draft.broker_fee_settlement_currency_id
                          ? 'The currency / country the fee is actually settled from.'
                          : 'Required when the fee is settled in a different currency.'}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
                <Grid size={12}>
                  <TextField
                    label="VAT Note (optional)"
                    fullWidth
                    multiline
                    minRows={2}
                    value={draft.broker_fee_vat_note}
                    onChange={(e) => setDraft(prev => ({ ...prev, broker_fee_vat_note: e.target.value }))}
                    placeholder="e.g. Cross-border — fee paid from SA in ZAR"
                    helperText="Recorded on the contract and shown on the contract detail + approval views."
                  />
                </Grid>
              </>
            )}

              </Grid>
            </Grid>

            {/* RIGHT COLUMN — when the fee is paid. */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Grid container spacing={3}>
            {/* ⚠⚠ THE WHOLE FEE-SCHEDULING BLOCK IS GATED ON A PER-TENANT SETTING.
                `enable_broker_fee_scheduling` (master DB, tenant_settings) reaches here via
                GET /contracts/settings/workflow -> ContractWizard -> ContractFormContext. A tenant
                that has not opted in sees none of this, and a broker fee behaves exactly as it did
                before Feature 30: one upfront figure, no schedule.

                ⚠ HIDDEN, NOT DISABLED — two reasons. A disabled-but-visible control advertises a
                capability the tenant has not enabled; and react-hook-form strips `disabled` fields
                from the payload, the trap already documented on the reference-data `type` field.

                ⚠ THIS IS AN AFFORDANCE, NOT THE ENFORCEMENT. Hiding the controls does nothing
                about a direct call to POST /broker-fees/contract/:id/schedule/generate, so the
                real refusal lives in routes/broker-fees.js. Note also that a contract captured
                while the setting was ON keeps its saved cadence if the tenant later switches OFF —
                the controls vanish but the data remains, and the server-side check is what makes
                that safe. */}
            {enableBrokerFeeScheduling && (
              <>
            {/* ============================ FEE SCHEDULING (Feature 30) ============================
                Placed AFTER the VAT group so the VAT control and its breakdown stay adjacent
                (the "control then its effect" rule above), and before the breakdown card because
                the cadence does not change the struck amount — it only decides when it is paid.

                ⚠ THE WHOLE BLOCK IS GATED, AND THE GATE'S STATE IS DERIVED — there is no
                `eligible_for_fee_scheduling` column and deliberately so. Every analogous gate
                (is_brokered, has_collateral, eligible_for_repricing, eligible_for_renewal) IS a
                persisted column on contract_master, but contract_broker_fees has none; adding one
                would cost a migration, a backfill, and a flag that can contradict the data it
                gates. Deriving the state from "is a cadence set?" makes disagreement impossible.

                Without the gate, four empty fields sat here permanently and "blank" had to mean
                BOTH "this fee is not scheduled" AND "I have not filled this in yet" — the user
                could not express the first, and the screen could not tell them apart.

                Switching it OFF clears every field, exactly as the Eligible-for-renewal gate does
                (InterestPaymentsStep.js:1225).

                ⚠ CORRECTED 2026-09-11 — "off" DOES NOT MEAN "no schedule". It means ONE payment
                event for the whole fee; the accrual rows are generated either way, because the fee
                is always smoothed over the term. The engine treats "no cadence" and Once-Off(999)
                identically (feeService generateFeePaymentDates: "Once-Off, or no frequency at all
                — a single event"), so this is the same code path, not a special case.

                The only fees the engine still skips are PRE-FEATURE-30 ones already settled under
                the old upfront model, identified by their backfill marker — a cutoff that lives in
                feeService, not here. A fee captured on this screen always accrues. */}
            {/* ⚠⚠ THE SWITCH IS DELIBERATELY INVERTED RELATIVE TO THE COLUMN. READ THIS
                    BEFORE CHANGING EITHER SIDE.
    
                        switch OFF (default) = paid at the beginning = paid_in_advance TRUE
                        switch ON            = paid at the end       = paid_in_advance FALSE
                        i.e.  paid_in_advance = !switchOn
    
                    Why: fees are usually paid upfront (user, 2026-09-11), so "beginning" is the
                    common case and the common case must be the default — while the DB column
                    `paid_in_advance BOOLEAN DEFAULT false` defaults to ARREAR. The two therefore
                    point opposite ways ON PURPOSE. Reading the switch as `paid_in_advance`
                    directly would invert every fee's payment dates: on a 12-month contract a
                    quarterly fee pays 01-01/04-01/07-01/10-01 in advance versus
                    03-31/06-30/09-30/12-31 in arrear — same 4 events, same amounts, wrong dates.
    
                    A single state-labelled switch replaces a 2-radio group: the radios cost three
                    lines and made the dialog scroll, while a bare checkbox (the version before
                    them) left the alternative unstated so "off" read as unanswered. Making BOTH
                    the label and the caption state-dependent keeps the answer explicit either way
                    — the switch always says which option is active.
    
                    ⚠ Safe on existing data, checked before changing the default: all 6 active dev
                    fees are paid_in_advance = false AND cadence-NULL, so none is scheduled and no
                    payment date can move. A previously-saved fee still round-trips correctly —
                    false renders as OFF/"beginning", which is what the new default means. */}
                <Grid size={12}>
                  <InlineGateToggle
                    label={draft.paid_in_advance === false
                      ? (<>Fee paid at the <strong>end</strong> of each period (in arrear)</>)
                      : (<>Fee paid at the <strong>beginning</strong> of each period (in advance)</>)}
                    checked={draft.paid_in_advance === false}
                    onChange={(e) => handleToggleFeeTiming(e.target.checked)}
                    /* ⚠ Both right-column toggles share this minHeight so they sit on the same
                       rhythm. InlineGateToggle defaults to 56 to line up with a TextField BESIDE
                       it; neither of these has one — they are full-width rows — so the default
                       only added dead space above the label. One of them used to override it to 0
                       and the other did not, which is what made the column read as unevenly
                       spaced. Change both together or neither. */
                    sx={{ minHeight: 0 }}
                  />
                </Grid>

            {/* ⚠⚠ THIS TOGGLE CONTROLS HOW THE FEE IS *PAID*, NOT WHETHER IT IS ACCOUNTED FOR.
                A fee schedule is ALWAYS generated — the GL requirement is that the fee is raised
                once but the expense is recognised over the contract term, so every fee produces
                ONE payment entry plus N accrual entries, N being the contract's tenor in months.
                Off, that single payment settles the whole fee (upfront when the switch above says
                "beginning"); on, it is split across a recurring cadence and can be repriced. This
                is the same shape as the interest-payment controls.

                ⚠ AN EARLIER CAPTION HERE READ "…no fee schedule is generated", WHICH WAS FALSE.
                It came from briefly modelling the toggle as a master switch over accrual itself,
                which would have meant a fee paid once-off upfront never had its expense spread —
                the GL would simply never see it. Do not restore that reading from an older comment
                or changelog entry. The engine matches this one: no cadence yields N accruals + 1
                payment (pinned by the ALWAYS-SCHEDULED and CADENCE-SHAPES-PAYMENTS-ONLY locks in
                tests/feeService.test.js), and the only fees skipped are pre-Feature-30 ones
                already settled under the old upfront model.

                ⚠ The whole block is additionally gated on the TENANT setting, so a tenant that has
                not opted in never sees any of this. */}
            <Grid size={12}>
              {/* ⚠ THE LABEL NAMES THE CURRENT STATE, IT IS NOT A STATIC INSTRUCTION.
                  Once-Off was removed from the frequency list (it contradicted the toggle it sat
                  inside and duplicated the off state exactly — the engine produced byte-identical
                  schedules for both), so switching this OFF is now the ONLY way to express "one
                  payment". A toggle reading "Pay the fee in instalments" while unchecked left that
                  state unnamed: the user could see what they were NOT doing, never what they WERE.
                  So the off state says "Once-Off" — and nothing more. "Once-Off" is the whole
                  meaning; an appended gloss like "— a single payment" restates the term rather
                  than adding to it.

                  This mirrors the timing toggle directly above, which already swaps its label
                  between "beginning" / "end" rather than stating one and leaving the other
                  implied — same component, same pattern, so the two read consistently. */}
              <InlineGateToggle
                label={feeSchedulingOn
                  ? 'Pay the fee in instalments'
                  : (<>Fee paid <strong>Once-Off</strong></>)}
                caption="The expense is accrued over the contract term either way."
                checked={feeSchedulingOn}
                onChange={(e) => handleToggleFeeScheduling(e.target.checked)}
                /* Matches the timing toggle above — see the note there. */
                sx={{ minHeight: 0 }}
              />
            </Grid>

            {feeSchedulingOn && (
              <>

                {/* ⚠ FREQUENCY AND TIMING ARE ONE QUESTION, SO THEY SIT TOGETHER.
                    "Quarterly" alone is an incomplete answer — the cadence is "Quarterly, paid at
                    the start of each quarter" or "...at the end". Verified against the engine on a
                    12-month contract with a 12,000 quarterly fee: arrear pays 03-31/06-30/09-30/
                    12-31, advance pays 01-01/04-01/07-01/10-01 — the SAME 4 events of 3,000 each,
                    with only the dates moved. Hence the timing radio directly under the frequency,
                    and the repricing pair separated below as a genuinely different cadence. */}
                {/* ⚠ alignItems: flex-start on BOTH cells of each frequency/date pair. The date
                    field reserves a helper row (see its helperText) and the select does not, so
                    without this the shorter control centres itself against the taller one and the
                    two inputs no longer share a top edge — the uneven look reported 2026-09-16.
                    Aligning to the top keeps the INPUTS level and lets the helper hang below. */}
                <Grid size={{ xs: 12, sm: 6 }} sx={{ alignSelf: 'flex-start' }}>
                  <FormControl fullWidth>
                    <InputLabel id="fee-frequency-label">Payment Frequency *</InputLabel>
                    <Select
                      labelId="fee-frequency-label"
                      label="Payment Frequency *"
                      value={draft.fee_frequency_id || ''}
                      onChange={(e) => handleSelectFeeFrequency(e.target.value)}
                    >
                      {feeFrequencyOptions.map((f) => (
                        <MenuItem key={f.id} value={f.id}>{f.frequency}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }} sx={{ alignSelf: 'flex-start' }}>
                  <TextField
                    label="Next Fee Date *"
                    type="date"
                    fullWidth
                    value={draft.next_fee_date || ''}
                    onChange={(e) => {
                      // Typing here hands ownership of the date to the user: from now on changing
                      // the frequency or the timing switch must not overwrite it.
                      setNextFeeDateOverridden(true);
                      setDraft(prev => ({ ...prev, next_fee_date: e.target.value }));
                    }}
                    InputLabelProps={{ shrink: true }}
                    error={!!draft.fee_frequency_id && !draft.next_fee_date}
                    /* Only two states say anything the user cannot see: that the field is required
                       (the DB CHECK enforces it too, but a 23514 is unmapped and would surface as
                       an opaque 500), and that a hand-typed date has stopped tracking the cadence.
                       The old "Derived — the first period opens/closes on this date" lines just
                       restated the beginning/end switch sitting directly above. */
                    /* ⚠ Falls back to ' ', not '' — the space RESERVES the helper row so this
                       field keeps the same height whether or not a message is showing, and the
                       two right-column rows stay aligned. Same trick as
                       InterestPaymentsStep.js:910. With '' the row collapsed and the column
                       visibly shifted as the user typed. */
                    helperText={
                      draft.fee_frequency_id && !draft.next_fee_date
                        ? 'Required — the first payment steps from this date.'
                        : nextFeeDateOverridden
                          ? 'Manually set — it will not follow the cadence.'
                          : ' '
                    }
                  />
                </Grid>


                {/* Repricing is a SEPARATE cadence from the payment cadence — how often the fee is
                    renegotiated, not how often it is paid. feeService keeps resolveRepricingMonths
                    deliberately apart from resolveFeeMonths for the same reason: "reading both
                    cadences in one place is how one silently starts driving the other."

                    No section heading: the two fields are named "Repricing Frequency" and "Next
                    Repricing Date", so a "Repricing (optional)" label above them only restated
                    what they already say. The distinction from RATE repricing is carried by the
                    field's own helper text, where it is actually needed. */}
                <Grid size={{ xs: 12, sm: 6 }} sx={{ alignSelf: 'flex-start' }}>
                  <FormControl fullWidth>
                    {/* shrink is REQUIRED alongside displayEmpty below. Without it MUI only
                        floats the label up once a value is selected, so with the empty value
                        rendering as "None" the label sat ON TOP of that text and the two
                        overlapped. */}
                    <InputLabel id="fee-repricing-frequency-label" shrink>Repricing Frequency</InputLabel>
                    <Select
                      labelId="fee-repricing-frequency-label"
                      label="Repricing Frequency"
                      /* notched keeps the outline's gap open to match the permanently-shrunk
                         label above; without it the border draws through the label text. */
                      notched
                      value={draft.repricing_frequency_id || ''}
                      onChange={(e) => handleSelectRepricingFrequency(e.target.value)}
                      /* displayEmpty + renderValue are REQUIRED to show "None" when nothing is
                         selected. MUI renders an empty value as a BLANK box — it does NOT fall
                         back to the matching MenuItem's label — so the None option was invisible
                         until the menu was opened, and the field read as unanswered rather than
                         as a deliberate "not repriced". */
                      displayEmpty
                      renderValue={(v) => {
                        if (!v) return 'None';
                        const f = repricingFrequencyOptions.find((x) => x.id === v);
                        return f ? f.frequency : '';
                      }}
                    >
                      <MenuItem value="">None</MenuItem>
                      {repricingFrequencyOptions.map((f) => (
                        <MenuItem key={f.id} value={f.id}>{f.frequency}</MenuItem>
                      ))}
                    </Select>
                    {/* ⚠ This is the FEE repricing cadence on contract_broker_fees — NOT
                        contract_master.repricing_frequency_id, which is the RATE repricing cadence
                        on the Interest & Payments step. Two columns, same name. The on-screen note
                        saying so was removed as noise (2026-09-16): the field sits inside the
                        broker dialog, so the context is already unambiguous to the user. The
                        warning matters to whoever edits THIS code, which is why it stays here. */}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }} sx={{ alignSelf: 'flex-start' }}>
                  <TextField
                    label={draft.repricing_frequency_id ? 'Next Repricing Date *' : 'Next Repricing Date'}
                    type="date"
                    fullWidth
                    value={draft.next_repricing_date || ''}
                    onChange={(e) => setDraft(prev => ({ ...prev, next_repricing_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    disabled={!draft.repricing_frequency_id}
                    error={!!draft.repricing_frequency_id && !draft.next_repricing_date}
                    helperText={
                      draft.repricing_frequency_id && !draft.next_repricing_date
                        /* chk_cbf_repricing_requires_date — same unmapped-23514 reasoning as above. */
                        ? 'Required when a repricing frequency is set.'
                        /* ' ' not '' — reserves the helper row, see the note on Next Fee Date. */
                        : ' '
                    }
                  />
                </Grid>
              </>
            )}
              </>
            )}

              </Grid>
            </Grid>

            {/* The VAT breakdown spans BOTH columns — it is the outcome of the whole dialog, not
                of either column alone. */}
            {draft.placement_fees_id && computeAmount(draft.broker_fees) != null && (
              <Grid size={12}>
                {/* T3-012: broker-fee VAT breakdown. Exclusive = capital × %,
                    VAT from the contract country, inclusive = exclusive + VAT. */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Exclusive (capital × %)</Typography>
                    <Typography variant="body2">{currencyCode} {formatNumber(computeAmount(draft.broker_fees))}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {draft.broker_fee_vat_treatment === VAT_TREATMENT_NO_VAT
                        ? 'VAT (0% — No-VAT)'
                        : `VAT${hasVatRate ? ` (${formatRatePct(vatPercent)}%)` : ''}`}
                    </Typography>
                    <Typography variant="body2">{currencyCode} {formatNumber(computeVatAmount(computeAmount(draft.broker_fees), draft.broker_fee_vat_treatment))}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight="bold">Inclusive</Typography>
                    <Typography variant="body2" fontWeight="bold">{currencyCode} {formatNumber(computeInclusive(computeAmount(draft.broker_fees), draft.broker_fee_vat_treatment))}</Typography>
                  </Box>
                  {/* T3-084: under a deliberate No-VAT choice the "no rate configured" warning
                      would imply the 0% is an accident, so it is gated on Standard and No-VAT
                      gets its own confirming caption. */}
                  {draft.broker_fee_vat_treatment === VAT_TREATMENT_NO_VAT ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      No-VAT treatment selected — VAT is 0% by explicit choice, not because a rate
                      is missing. Inclusive equals exclusive.
                      {draft.broker_fee_settled_in_other_ccy && draft.broker_fee_settlement_currency_id
                        ? ` Settled in ${(refData.currencies || []).find(c => c.id === draft.broker_fee_settlement_currency_id)?.currency_code || ''}.`
                        : ''}
                    </Typography>
                  ) : !hasVatRate && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                      No VAT rate configured for this contract's country (Reference Data → VAT Rates).
                      VAT treated as 0% — inclusive equals exclusive.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled()}>
            {isBrokered ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Remove Broker"
        message="Are you sure you want to remove the broker? Broker fee will be cleared."
        confirmText="Remove"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false, onConfirm: null })}
      />
    </Box>
  );
}

export default BrokerSection;
